/**
 * TargetingRuleBuilder Component - Build dynamic targeting rules
 *
 * Features:
 * - AND/OR combinator selection
 * - Add/remove conditions
 * - Property/operator/value selection per condition
 * - Dynamic operator options based on property type
 * - Dynamic value inputs based on operator
 */

import React, { useState, useEffect, useCallback } from 'react';
import * as adminTargetingApi from '../../../api/adminTargeting';
import './TargetingRuleBuilder.css';

function TargetingRuleBuilder({ value, onChange }) {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize rules with default structure if empty
  const rules = value || { combinator: 'AND', conditions: [] };

  // Fetch available properties on mount
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const result = await adminTargetingApi.getProperties();
        setProperties(result.data || []);
      } catch (err) {
        setError('Failed to load targeting properties');
        console.error('Failed to load properties:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  const handleCombinatorChange = useCallback((combinator) => {
    onChange({ ...rules, combinator });
  }, [rules, onChange]);

  const handleAddCondition = useCallback(() => {
    const newCondition = {
      property: properties[0]?.db_column || '',
      operator: 'equals',
      value: ''
    };
    onChange({
      ...rules,
      conditions: [...rules.conditions, newCondition]
    });
  }, [rules, properties, onChange]);

  const handleRemoveCondition = useCallback((index) => {
    const newConditions = rules.conditions.filter((_, i) => i !== index);
    onChange({ ...rules, conditions: newConditions });
  }, [rules, onChange]);

  const handleConditionChange = useCallback((index, field, newValue) => {
    const newConditions = [...rules.conditions];
    newConditions[index] = { ...newConditions[index], [field]: newValue };

    // Reset operator and value when property changes
    if (field === 'property') {
      const prop = properties.find(p => p.db_column === newValue);
      newConditions[index].operator = prop?.operators?.[0] || 'equals';
      newConditions[index].value = '';
    }

    // Reset value when operator changes to 'between'
    if (field === 'operator' && newValue === 'between') {
      newConditions[index].value = ['', ''];
    } else if (field === 'operator' && rules.conditions[index].operator === 'between') {
      newConditions[index].value = '';
    }

    onChange({ ...rules, conditions: newConditions });
  }, [rules, properties, onChange]);

  const getPropertyByColumn = (dbColumn) => {
    return properties.find(p => p.db_column === dbColumn);
  };

  if (loading) {
    return <div className="targeting-rule-builder targeting-rule-builder--loading">Loading properties...</div>;
  }

  if (error) {
    return <div className="targeting-rule-builder targeting-rule-builder--error">{error}</div>;
  }

  return (
    <div className="targeting-rule-builder">
      <div className="targeting-rule-builder__header">
        <span className="targeting-rule-builder__label">Match</span>
        <select
          value={rules.combinator}
          onChange={(e) => handleCombinatorChange(e.target.value)}
          className="targeting-rule-builder__combinator"
        >
          <option value="AND">ALL conditions (AND)</option>
          <option value="OR">ANY condition (OR)</option>
        </select>
      </div>

      <div className="targeting-rule-builder__conditions">
        {rules.conditions.length === 0 ? (
          <div className="targeting-rule-builder__empty">
            No conditions - message will target all users
          </div>
        ) : (
          rules.conditions.map((condition, index) => (
            <ConditionRow
              key={index}
              condition={condition}
              properties={properties}
              property={getPropertyByColumn(condition.property)}
              onChange={(field, value) => handleConditionChange(index, field, value)}
              onRemove={() => handleRemoveCondition(index)}
            />
          ))
        )}
      </div>

      <button
        type="button"
        className="targeting-rule-builder__add"
        onClick={handleAddCondition}
      >
        + Add Condition
      </button>
    </div>
  );
}

function ConditionRow({ condition, properties, property, onChange, onRemove }) {
  const operators = property?.operators || ['equals'];
  const propertyType = property?.property_type || 'string';

  return (
    <div className="condition-row">
      <select
        value={condition.property}
        onChange={(e) => onChange('property', e.target.value)}
        className="condition-row__property"
      >
        {properties.map(p => (
          <option key={p.db_column} value={p.db_column}>
            {p.name}
          </option>
        ))}
      </select>

      <select
        value={condition.operator}
        onChange={(e) => onChange('operator', e.target.value)}
        className="condition-row__operator"
      >
        {operators.map(op => (
          <option key={op} value={op}>
            {formatOperator(op)}
          </option>
        ))}
      </select>

      <ValueInput
        propertyType={propertyType}
        operator={condition.operator}
        value={condition.value}
        onChange={(value) => onChange('value', value)}
      />

      <button
        type="button"
        className="condition-row__remove"
        onClick={onRemove}
        aria-label="Remove condition"
      >
        &times;
      </button>
    </div>
  );
}

function ValueInput({ propertyType, operator, value, onChange }) {
  // Handle 'between' operator with two values
  if (operator === 'between') {
    const values = Array.isArray(value) ? value : ['', ''];
    return (
      <div className="value-input value-input--between">
        <input
          type={propertyType === 'date' ? 'date' : 'text'}
          value={values[0] || ''}
          onChange={(e) => onChange([e.target.value, values[1]])}
          placeholder="From"
        />
        <span>and</span>
        <input
          type={propertyType === 'date' ? 'date' : 'text'}
          value={values[1] || ''}
          onChange={(e) => onChange([values[0], e.target.value])}
          placeholder="To"
        />
      </div>
    );
  }

  // Boolean type
  if (propertyType === 'boolean') {
    return (
      <select
        value={String(value)}
        onChange={(e) => onChange(e.target.value === 'true')}
        className="value-input"
      >
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }

  // Date type
  if (propertyType === 'date') {
    if (operator === 'within_last_days') {
      return (
        <input
          type="number"
          value={value || ''}
          onChange={(e) => onChange(parseInt(e.target.value, 10) || '')}
          placeholder="Days"
          min="1"
          className="value-input value-input--number"
        />
      );
    }
    return (
      <input
        type="date"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="value-input"
      />
    );
  }

  // Number type
  if (propertyType === 'number') {
    return (
      <input
        type="number"
        value={value || ''}
        onChange={(e) => onChange(parseFloat(e.target.value) || '')}
        className="value-input value-input--number"
      />
    );
  }

  // String type (default)
  return (
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Value"
      className="value-input"
    />
  );
}

function formatOperator(op) {
  const labels = {
    equals: 'equals',
    not_equals: 'does not equal',
    contains: 'contains',
    greater_than: 'is greater than',
    less_than: 'is less than',
    between: 'is between',
    before: 'is before',
    after: 'is after',
    within_last_days: 'within last N days'
  };
  return labels[op] || op;
}

export default TargetingRuleBuilder;
